import { PeriodBreakdownItem, StatsResponse, WeekBreakdownItem } from '../../models/stats.models';
import {
  MetricMode,
  formatDistributionPercent,
  formatDistributionRawValue,
  formatStackedChartTooltipValue,
  formatYAxisTick,
  safeZoneArray,
  secondsToChartHours,
} from '../../pages/training-log/training-log.helpers';
import { ZONE_CONFIGS } from '../../helpers/general.helpers';
import { getDistanceAxisDomain, getDurationAxisDomain } from '../season-overview-chart/season-overview-chart.helpers';

export type { MetricMode };
export { formatDistributionPercent, formatDistributionRawValue, formatYAxisTick };
export { formatStackedChartTooltipValue as formatZoneChartValue };

export interface StackedZoneChartRow {
  label: string;
  z0: number;
  z1: number;
  z2: number;
  z3: number;
  z4: number;
  z5: number;
}

export interface ZoneDistributionItem {
  zone: number;
  name: string;
  percent: number;
  value: number;
  rawValue: number;
  color: string;
}

export const ZONE_DATA_KEYS = ['z0', 'z1', 'z2', 'z3', 'z4', 'z5'] as const;

const breakdownToZoneRow = (breakdown: number[] | undefined, mode: MetricMode): Omit<StackedZoneChartRow, 'label'> => {
  const safe = safeZoneArray(breakdown);
  const values = mode === 'duration' ? safe.map(secondsToChartHours) : safe;

  return {
    z0: values[0],
    z1: values[1],
    z2: values[2],
    z3: values[3],
    z4: values[4],
    z5: values[5],
  };
};

export const buildWeeklyZoneChartData = (weeks: WeekBreakdownItem[], mode: MetricMode): StackedZoneChartRow[] => {
  return weeks.map((week) => ({
    label: String(week.index),
    ...breakdownToZoneRow(mode === 'duration' ? week.totalDurationBreakdown : week.totalDistanceBreakdown, mode),
  }));
};

export const buildPeriodZoneChartData = (periods: PeriodBreakdownItem[], mode: MetricMode): StackedZoneChartRow[] => {
  return periods.map((period) => ({
    label: `P${period.index}`,
    ...breakdownToZoneRow(mode === 'duration' ? period.totalDurationBreakdown : period.totalDistanceBreakdown, mode),
  }));
};

export const buildZoneDistributionData = (
  durationBreakdown: number[] | undefined,
  distanceBreakdown: number[] | undefined,
  mode: MetricMode
): ZoneDistributionItem[] => {
  const breakdown = mode === 'duration' ? safeZoneArray(durationBreakdown) : safeZoneArray(distanceBreakdown);
  const total = breakdown.reduce((sum, value) => sum + value, 0);

  return ZONE_CONFIGS.map((config, index) => ({
    zone: index,
    name: `Zone ${index}`,
    percent: total > 0 ? (breakdown[index] / total) * 100 : 0,
    value: total > 0 ? (breakdown[index] / total) * 100 : 0,
    rawValue: breakdown[index],
    color: config.barColor,
  }));
};

export const getStackedBarTotal = (row: StackedZoneChartRow): number => {
  return row.z0 + row.z1 + row.z2 + row.z3 + row.z4 + row.z5;
};

export const getStackedBarYDomain = (rows: StackedZoneChartRow[], mode: MetricMode): [number, number] => {
  const totals = rows.map(getStackedBarTotal);
  return mode === 'duration' ? getDurationAxisDomain(totals) : getDistanceAxisDomain(totals);
};

export const hasZoneData = (stats: StatsResponse | null): boolean => {
  if (!stats) {
    return false;
  }

  const durationTotal = safeZoneArray(stats.totalDurationBreakdown).reduce((sum, value) => sum + value, 0);
  const distanceTotal = safeZoneArray(stats.totalDistanceBreakdown).reduce((sum, value) => sum + value, 0);

  return durationTotal > 0 || distanceTotal > 0;
};
